import React, { useState, useEffect, useRef } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { firebase } from '../../../services/firebase';
import { ref } from 'firebase/storage';
import { storage } from '../../../services/firebase';
import { Button } from 'primereact/button';
import { Steps } from 'primereact/steps';
import { Toast } from 'primereact/toast';
import { Accordion, AccordionTab } from 'primereact/accordion';
import { InputSwitch } from 'primereact/inputswitch';
import { Formik, Form, useFormikContext } from 'formik';
import { useAuth } from '../../../hooks/useAuth';
import { useShopConfig } from '../../../hooks/useShopConfig';
import { useBusinessConfig } from '../../../hooks/useBusinessConfig';
import { useBusinessServices } from './hooks/useBusinessServices';
import { INITIAL_VALUES, SETUP_STEPS, VALIDATION_SCHEMA, SHOP_TYPES, PAYMENT_METHODS, SERVICE_FORM_INITIALS } from './constant';
import CombinedInformationSection from './components/CombinedInformationSection';
import ServiceForm from '../../../components/forms/ServiceForm/ServiceForm';
import FoodForm from '../../../components/forms/FoodForm/FoodForm';
import TransportForm from '../../../components/forms/TransportForm/TransportForm';
import TechForm from '../../../components/forms/TechForm/TechForm';
import RetailForm from '../../../components/forms/RetailForm/RetailForm';
import RealEstateForm from '../../../components/forms/RealEstateForm/RealEstateForm';
import ProfessionalForm from '../../../components/forms/ProfessionalForm/ProfessionalForm';
import EducationForm from '../../../components/forms/EducationForm/EducationForm';
import HealthWellnessForm from '../../../components/forms/HealthWellnessForm/HealthWellnessForm';
import EntertainmentForm from '../../../components/forms/EntertainmentLeisureForm/EntertainmentLeisureForm';
import BusinessHoursForm from '../../../components/forms/BusinessHoursForm/BusinessHoursForm';
import PaymentMethodForm from '../../../components/forms/PaymentMethodForm/PaymentMethodForm';

import './styles.css';

const DynamicInitialValues = () => {
  const { values, setFieldValue } = useFormikContext();

  useEffect(() => {
    if (Array.isArray(values.shopType)) {
      const newlySelected = values.shopType.filter(st => st.startsWith('SV_'));
      newlySelected.forEach(st => {
        Object.keys(SERVICE_FORM_INITIALS).forEach(key => {
          const fieldName = `${key}_${st}`;
          if (values[fieldName] === undefined) {
            setFieldValue(fieldName, SERVICE_FORM_INITIALS[key]);
          }
        });
      });
    }
  }, [values.shopType, setFieldValue]);

  return null;
};

const BusinessTailoring = () => {
  const { user } = useAuth();
  const { handleSaveClick } = useShopConfig();
  const { handleBusinessSaveClick } = useBusinessConfig();
  const { shopType, loading: shopTypeLoading, error: shopTypeError, getServicesByType } = useBusinessServices(user);
  const [activeStep, setActiveStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [userData, setUserData] = useState(null);
  const toast = useRef(null);
  const stateRef = useRef({});

  useEffect(() => {
    const fetchUserData = async () => {
      if (!user) return;

      try {
        const userDocRef = doc(firebase, 'users', user.uid);
        const userDoc = await getDoc(userDocRef);
        if (userDoc.exists()) {
          const userData = userDoc.data();
          setUserData(userData);
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
        toast.current?.show({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to fetch user data. Please try again.',
          life: 3000
        });
      }
    };

    fetchUserData();
  }, [user]);

  const handleSubmit = async (values, { setSubmitting }) => {
    if (!user) return;

    try {
      // If this is the Save and Skip action, modify values
      if (Array.isArray(values) && values.length === 0) {
        // This indicates we're skipping category customization
        values = {
          ...values,
        };
      }

      // First save shop config
      const shopSuccess = await handleSaveClick(values, true);

      if (!shopSuccess) {
        // If shop config failed, show error and return
        toast.current?.show({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to save shop configuration. Please try again.',
          life: 3000
        });
        return;
      }

      // Then save business config
      // Pass all form values to business config
      const businessValues = {
        ...values
      };

      const businessSuccess = await handleBusinessSaveClick(businessValues, true);

      if (businessSuccess) {
        // Only show success toast after successful submission
        toast.current?.show({
          severity: 'success',
          summary: 'Success',
          detail: 'Settings updated successfully!',
          life: 3000
        });
      } else {
        // If business config failed, show error
        toast.current?.show({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to save business configuration. Please try again.',
          life: 3000
        });
      }
    } catch (error) {
      console.error('Error during first-time setup:', error);
      // Show generic error toast if something unexpected happens
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to complete setup. Please try again.',
        life: 3000
      });
    } finally {
      setSubmitting(false);
      setIsSubmitting(false);
    }
  };

  const getSteps = (shopType) => {
    const baseSteps = SETUP_STEPS.base;
    const shopTypes = Array.isArray(shopType) ? shopType : shopType ? [shopType] : [];

    // Get all unique categories from selected shop types
    const categories = shopTypes.reduce((acc, type) => {
      // Find the category by checking each category's items
      const category = Object.values(SHOP_TYPES).find(category =>
        category.items && category.items.some(item => item.value === type)
      );

      if (category && !acc.includes(category.label)) {
        acc.push(category.label);
      }
      return acc;
    }, []);

    // Create steps for each category
    const groupedSteps = categories.map(category => ({
      label: category,
      icon: 'pi pi-file-arrow-up',
      description: `Configure your ${category.toLowerCase().replace(' & ', ' and ')} details`,
      category: category,
      shopTypes: shopTypes.filter(type => {
        const categoryObj = Object.values(SHOP_TYPES).find(cat =>
          cat.items && cat.items.some(item => item.value === type)
        );
        return categoryObj?.label === category;
      })
    }));

    return [...baseSteps, ...groupedSteps];
  };

  const renderStepContent = (step, values, errors, touched, handleChange, setFieldValue, setFieldError) => {
    const steps = getSteps(values.shopType);
    const currentStep = steps[step];
    switch (step) {
      case 0:
        return <CombinedInformationSection
          values={values}
          errors={errors}
          touched={touched}
          handleChange={handleChange}
          setFieldValue={setFieldValue}
          setFieldError={(field, error) => setFieldError(field, error, true)}
          stateRef={stateRef}
        />;
      default:
        if (currentStep.category) {
          const categoryForms = {
            'Service': ServiceForm,
            'Food & Beverage': FoodForm,
            'Transportation & Automotive': TransportForm,
            'Technology & Online': TechForm,
            'Retail': RetailForm,
            'Real Estate & Housing': RealEstateForm,
            'Professional Services': ProfessionalForm,
            'Education & Training': EducationForm,
            'Health & Wellness': HealthWellnessForm,
            'Entertainment & Leisure': EntertainmentForm
          };

          const FormComponent = categoryForms[currentStep.category];

          if (FormComponent && currentStep.shopTypes) {
            return (
              <div>
                <div className="shared-settings">
                  {values.sharedSettings && (
                    <div className="shared-forms">
                      <div className="shared-form-section">
                        <BusinessHoursForm
                          shopType={currentStep.shopTypes[0]}
                          formState={values}
                          setFormState={(field, value) => setFieldValue(field, value)}
                        />
                      </div>

                      <div className="shared-form-section">
                        <PaymentMethodForm
                          shopType={currentStep.shopTypes[0]}
                          formState={values}
                          setFormState={(field, value) => setFieldValue(field, value)}
                          paymentMethods={PAYMENT_METHODS}
                        />
                      </div>
                    </div>
                  )}
                </div>

                <Accordion multiple activeIndex={[0]}>
                  {currentStep.shopTypes.map((shopType, index) => (
                    <AccordionTab
                      key={shopType}
                      header={SHOP_TYPES.find(cat =>
                        cat.items && cat.items.some(item => item.value === shopType)
                      )?.items.find(item => item.value === shopType)?.label || shopType}
                    >
                      <FormComponent
                        shopType={shopType}
                        formState={values}
                        setFormState={(field, value) => setFieldValue(field, value)}
                      />

                      {!values.sharedSettings && (
                        <div className="individual-settings">
                          <div className="individual-form-section">
                            <BusinessHoursForm
                              shopType={shopType}
                              formState={values}
                              setFormState={(field, value) => setFieldValue(field, value)}
                            />
                          </div>

                          <div className="individual-form-section">
                            <PaymentMethodForm
                              shopType={shopType}
                              formState={values}
                              setFormState={(field, value) => setFieldValue(field, value)}
                              paymentMethods={PAYMENT_METHODS}
                            />
                          </div>
                        </div>
                      )}
                    </AccordionTab>
                  ))}
                </Accordion>
              </div>
            );
          }
        }
        return null;
    }
  };

  const getInitialValues = () => {
    // Start with default values
    const initialValues = { ...INITIAL_VALUES };

    // Add user data if available
    if (userData) {
      // Copy basic user data fields
      Object.keys(userData).forEach(key => {
        if (INITIAL_VALUES[key] !== undefined) {
          initialValues[key] = userData[key];
        }
      });

      // Handle shopType specifically
      if (userData.shopType) {
        const rawShopTypes = Array.isArray(userData.shopType) ? userData.shopType : [userData.shopType];
        initialValues.shopType = rawShopTypes.filter(st => st);
      }
    }

    // Handle nested shopType data
    if (shopType && typeof shopType === 'object') {
      // Filter out id and updatedAt fields
      const filteredShopType = {
        ...shopType
      };
      delete filteredShopType.id;
      delete filteredShopType.updatedAt;

      // Process all category data dynamically
      Object.keys(filteredShopType).forEach(category => {
        if (Array.isArray(filteredShopType[category])) {
          filteredShopType[category].forEach(serviceObj => {
            Object.keys(serviceObj).forEach(serviceKey => {
              const serviceData = serviceObj[serviceKey];
              // Add serviceKey suffix to all fields
              Object.keys(serviceData).forEach(field => {
                initialValues[`${field}_${serviceKey}`] = serviceData[field];
              });
            });
          });
        }
      })
    }

    return initialValues;
  };

  const uploadImageToFirebase = (file, userId, guestId, onProgress) => {
    const timestamp = Date.now();
    const rawName = (file && typeof file.name === 'string') ? file.name : 'upload';
    const safeFileName = rawName.replace(/[^a-zA-Z0-9._-]/g, '_');
    // Use the chat-attachments path as defined in Firebase Storage rules
    const storageRef = ref(storage, `chat-attachments/${userId}/${guestId}/${timestamp}_${safeFileName}`);
    if (!(file.type && file.type.startsWith('image/'))) {
      // ... rest of the function remains the same ...
    }
  };

  return (
    <div className="business-tailoring">
      <Toast ref={toast} />

      <Formik
        initialValues={getInitialValues()}
        validationSchema={VALIDATION_SCHEMA}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ isValid, dirty, values, errors, touched, handleChange, setFieldValue, setSubmitting, setFieldTouched, setFieldError }) => {
          const steps = getSteps(values.shopType);

          return (
            <Form className="setup-form">
              <DynamicInitialValues />
              <Steps
                model={steps.map(step => ({
                  ...step,
                  icon: undefined
                }))}
                activeIndex={activeStep}
                readOnly={false}
                className="setup-steps"
                onSelect={(e) => {
                  setActiveStep(e.index);
                }}
              />
              {renderStepContent(activeStep, values, errors, touched, handleChange, setFieldValue, setFieldError)}
              <div className="form-navigation">


                {steps.length === 1 ? (
                  <Button
                    type="submit"
                    label="Update Settings"
                    icon="pi pi-check"
                    loading={isSubmitting}
                    className="p-button-primary"
                  />
                ) : (
                  <>
                    <Button
                      type="button"
                      label={activeStep < steps.length - 1 ? 'Next' : 'Update Settings'}
                      icon={activeStep < steps.length - 1 ? 'pi pi-arrow-right' : 'pi pi-check'}
                      onClick={activeStep < steps.length - 1 ?
                        async () => {
                          if (activeStep === 0) {
                            // Touch all required fields
                            const requiredFields = ['shopName', 'contactEmail', 'contactPhone', 'storeAddress', 'directions'];
                            requiredFields.forEach(field => {
                              setFieldValue(field, values[field]);
                              setFieldTouched(field, true);
                            });

                            // Validate the first step using Formik's validation with VALIDATION_SCHEMA
                            try {
                              await VALIDATION_SCHEMA.validate(values, { abortEarly: false });
                            } catch (error) {
                              // Show the first validation error in toast
                              const firstError = error.inner[0]?.message || 'Please fill in all required fields';
                              toast.current?.show({
                                severity: 'error',
                                summary: 'Validation Error',
                                detail: firstError,
                                life: 3000
                              });

                              // Also show error messages next to each invalid field
                              error.inner.forEach(err => {
                                const field = err.path;
                                if (field) {
                                  setFieldTouched(field, true);
                                }
                              });
                              return;
                            }

                            // Check for shop type selection
                            if (!values.shopType || (Array.isArray(values.shopType) && values.shopType.length === 0)) {
                              toast.current?.show({
                                severity: 'error',
                                summary: 'Validation Error',
                                detail: 'Please select at least one shop type',
                                life: 3000
                              });
                              return;
                            }
                          }
                          setActiveStep(activeStep + 1);
                        } :
                        async () => {
                          await handleSubmit(values, { setSubmitting });
                        }
                      }
                      loading={isSubmitting}
                      className="p-button-primary"
                    />
                    {activeStep === 0 && (
                      <Button
                        type="button"
                        label="Update this only"
                        icon="pi pi-fast-forward"
                        onClick={async () => {
                          const formik = { values: values, setSubmitting };
                          await handleSubmit(values, formik);
                        }}
                        className="p-button-text"
                      />
                    )}
                  </>
                )}
                {activeStep > 0 && (
                  <Button
                    type="button"
                    label="Back"
                    icon="pi pi-arrow-left"
                    onClick={() => setActiveStep(activeStep - 1)}
                    className="p-button-text"
                  />
                )}
              </div>
            </Form>
          );
        }}
      </Formik>
    </div>
  );
};

export default BusinessTailoring;
